import { IVariant } from 'services/variant';
import { DiscountEntry } from 'utilities/discount-entry';

export type ISaleProductState = IVariant &
  DiscountEntry & {
  quantity: number;
  unitPrice: number;
  discountCost?: number;
};

export enum ActionKind {
  SetSaleProducts = 'SET_SALE_PRODUCTS',
  AddSaleProduct = 'ADD_SALE_PRODUCT',
  RemoveSaleProduct = 'REMOVE_SALE_PRODUCT',
  EditSaleProduct = 'EDIT_SALE_PRODUCT',
  SetWarehouseId = 'SET_WAREHOUSE_ID',
  SetVat = 'SET_VAT',
  SetShipment = 'SET_SHIPMENT',
  SetDiscount = 'SET_DISCOUNT',
  SetInvoiceDiscountEntry = 'SET_INVOICE_DISCOUNT_ENTRY',
  SetPaidInCash = 'SET_PAID_IN_CASH',
  SetPayNow = 'SET_PAY_NOW',
  SetDue = 'SET_DUE',
}

export type Action =
  | { type: ActionKind.SetSaleProducts; payload: ISaleProductState[] }
  | { type: ActionKind.AddSaleProduct; payload: ISaleProductState }
  | { type: ActionKind.RemoveSaleProduct; payload: string }
  | { type: ActionKind.EditSaleProduct; payload: ISaleProductState }
  | { type: ActionKind.SetWarehouseId; payload: string }
  | { type: ActionKind.SetVat; payload: number }
  | { type: ActionKind.SetShipment; payload: number }
  | { type: ActionKind.SetDiscount; payload: number }
  | { type: ActionKind.SetInvoiceDiscountEntry; payload: DiscountEntry }
  | { type: ActionKind.SetPaidInCash; payload: boolean }
  | { type: ActionKind.SetPayNow; payload: boolean }
  | { type: ActionKind.SetDue; payload: boolean };

interface IState {
  saleProducts: ISaleProductState[];
  warehouseId: string | undefined;
  vat: number;
  shipment: number;
  discountCost: number;
  invoiceDiscountEntry: DiscountEntry;
  paidInCash: boolean;
  payNow: boolean;
  fullDue: boolean;
}

export const initialState: IState = {
  saleProducts: [],
  warehouseId: undefined,
  vat: 0,
  shipment: 0,
  discountCost: 0,
  invoiceDiscountEntry: { discountEntryValue: null, discountEntryType: null },
  paidInCash: true,
  payNow: false,
  fullDue: false,
};

export const productSaleReducer = (state: IState, action: Action) => {
  switch (action.type) {
    case ActionKind.SetSaleProducts:
      return { ...state, saleProducts: action.payload };
    case ActionKind.AddSaleProduct:
      const isAlreadyExist = state.saleProducts.find(
        (variant) => variant.productVariantId === action.payload.productVariantId
      );
      if (isAlreadyExist) return state;
      return { ...state, saleProducts: [...state.saleProducts, action.payload] };
    case ActionKind.RemoveSaleProduct:
      const variants = state.saleProducts.filter(
        (variant) => variant.productVariantId !== action.payload
      );
      return { ...state, saleProducts: variants };
    case ActionKind.EditSaleProduct:
      const modifiedProducts = state.saleProducts.map((variant) =>
        variant.productVariantId === action.payload.productVariantId
          ? { ...action.payload }
          : { ...variant }
      );
      return { ...state, saleProducts: modifiedProducts };
    case ActionKind.SetWarehouseId:
      return { ...state, warehouseId: action.payload };
    case ActionKind.SetVat:
      return { ...state, vat: action.payload };
    case ActionKind.SetShipment:
      return { ...state, shipment: action.payload };
    case ActionKind.SetDiscount:
      return { ...state, discountCost: action.payload };
    case ActionKind.SetInvoiceDiscountEntry:
      return { ...state, invoiceDiscountEntry: action.payload };
    case ActionKind.SetPaidInCash:
      return { ...state, paidInCash: action.payload };
    case ActionKind.SetPayNow:
      return { ...state, payNow: action.payload };
    case ActionKind.SetDue:
      return { ...state, fullDue: action.payload };
    default:
      return state;
  }
};
